import { css } from '@emotion/react';
import React, { ReactNode } from 'react';

import { Box, BoxProps } from '../Box';
import type { ResponsiveValue } from './types';
import {
  generateAlignItems,
  generateColumnGapStyles,
  generateGapStyles,
  generateGridColumns,
  generateJustifyItems,
  generateRowGapStyles,
} from './utils';

export interface GridProps extends Omit<BoxProps, 'display'> {
  /**
   * Number of columns at different breakpoints
   */
  columns?: ResponsiveValue<number>;
  /**
   * Gap between grid items using space tokens
   */
  gap?: ResponsiveValue<keyof typeof import('../shared/styles').space>;
  /**
   * Row gap between grid items using space tokens
   */
  rowGap?: ResponsiveValue<keyof typeof import('../shared/styles').space>;
  /**
   * Column gap between grid items using space tokens
   */
  columnGap?: ResponsiveValue<keyof typeof import('../shared/styles').space>;
  /**
   * Size of auto-generated rows
   */
  autoRows?: string;
  /**
   * Size of auto-generated columns
   */
  autoColumns?: string;
  /**
   * Grid template areas for named layouts
   */
  templateAreas?: ResponsiveValue<string>;
  /**
   * Align items in the grid
   */
  alignItems?: ResponsiveValue<'start' | 'end' | 'center' | 'stretch'>;
  /**
   * Justify items in the grid
   */
  justifyItems?: ResponsiveValue<'start' | 'end' | 'center' | 'stretch'>;
  /**
   * Justify content in the grid
   */
  justifyContent?: ResponsiveValue<
    | 'start'
    | 'end'
    | 'center'
    | 'stretch'
    | 'space-between'
    | 'space-around'
    | 'space-evenly'
  >;
  /**
   * Grid children
   */
  children: ReactNode;
}

const baseGridStyles = css`
  display: grid;
`;

const Grid = ({
  columns = 12,
  gap,
  rowGap,
  columnGap,
  autoRows,
  autoColumns,
  templateAreas,
  alignItems,
  justifyItems,
  justifyContent,
  children,
  ...props
}: GridProps) => {
  const gridStyles = [
    baseGridStyles,
    columns && generateGridColumns(columns),
    gap && generateGapStyles(gap),
    rowGap && generateRowGapStyles(rowGap),
    columnGap && generateColumnGapStyles(columnGap),
    alignItems && generateAlignItems(alignItems),
    justifyItems && generateJustifyItems(justifyItems),
    autoRows &&
      css`
        grid-auto-rows: ${autoRows};
      `,
    autoColumns &&
      css`
        grid-auto-columns: ${autoColumns};
      `,
    templateAreas &&
      css`
        grid-template-areas: ${typeof templateAreas === 'string'
          ? templateAreas
          : templateAreas._};
      `,
    justifyContent &&
      css`
        justify-content: ${typeof justifyContent === 'string'
          ? justifyContent
          : justifyContent._};
      `,
  ].filter(Boolean);

  return (
    <Box css={gridStyles} {...props}>
      {children}
    </Box>
  );
};

export default Grid;
